import {
  CategoriesResponse,
  Category,
} from "~/types/Data/ProductManagement/Categories";

type FetchCategoriesOptions = {
  include_media?: boolean;
  filter?: { name: "category"; itemId: string };
};

export default function useCategories() {
  const nuxtApp = useNuxtApp();

  const { data: categories } = useNuxtData<Category[] | undefined>(
    "categories",
  );
  const { data: subcategories } = useNuxtData<Category[] | undefined>(
    "sub_categories",
  );
  const { data: collections } = useNuxtData<Category[] | undefined>(
    "collections",
  );

  const fetchCategories = async (options?: FetchCategoriesOptions) => {
    if (nuxtApp.$features?.categories?.is_enabled) {
      const query = options?.include_media ? "?include=media" : "/roots";
      const filter = options?.filter
        ? `${options?.include_media ? "&" : ""}filter[${options.filter.name}]=${options.filter.itemId}`
        : "";

      const { res } = await useApi<CategoriesResponse>(
        `/api/v2/categories${query}${filter}`,
      );
      if (
        nuxtApp.$features.categories.configs?.show_empty_categories ||
        (res?.data && res.data.length > 0)
      ) {
        return res?.data;
      }
    }
  };

  const fetchSubCategories = async (options?: FetchCategoriesOptions) => {
    if (
      nuxtApp.$features?.categories?.is_enabled &&
      nuxtApp.$features.categories.configs?.have_sub_categories
    ) {
      const query = options?.include_media ? ",media" : "";
      const filter = options?.filter
        ? `&filter[${options.filter.name}]=${options.filter.itemId}`
        : "";

      const { res } = await useApi<CategoriesResponse>(
        `/api/v2/categories/subcategories?include=collections${query}${filter}`,
      );
      if (
        nuxtApp.$features.categories.configs?.show_empty_categories ||
        (res?.data && res.data.length > 0)
      ) {
        return res?.data;
      }
    }
  };

  const fetchCollections = async (options?: FetchCategoriesOptions) => {
    if (
      nuxtApp.$features?.categories?.is_enabled &&
      nuxtApp.$features.categories.configs?.have_collections
    ) {
      const query = options?.include_media ? "?include=media" : "";
      const filter = options?.filter
        ? `${options?.include_media ? "&" : ""}filter[${options.filter.name}]=${options.filter.itemId}`
        : "";

      const { res } = await useApi<CategoriesResponse>(
        `/api/v2/categories/collections${query}${filter}`,
      );
      if (
        nuxtApp.$features.categories.configs?.show_empty_categories ||
        (res?.data && res.data.length > 0)
      ) {
        return res?.data;
      }
    }
  };

  const categoriesMap = computed(
    () => new Map(categories.value?.map((cat) => [cat.id, cat]) ?? []),
  );

  const subcategoriesMap = computed(() => {
    const map = new Map<string, { subcategory: Category; parentId: string }>();

    subcategories.value?.forEach((subcategory) => {
      map.set(subcategory.id, {
        subcategory,
        parentId: subcategory.parent_category?.id ?? "",
      });
    });

    return map;
  });

  const collectionsMap = computed(() => {
    const map = new Map<
      string,
      { collection: Category; parentId: string; grandParentId?: string }
    >();

    subcategories.value?.forEach((subcategory) => {
      subcategory.children.forEach((collection) => {
        map.set(collection.id, {
          collection,
          parentId: subcategory.id,
          grandParentId: subcategory.parent_category?.id,
        });
      });
    });
    return map;
  });

  return {
    categories,
    subcategories,
    collections,
    categoriesMap,
    subcategoriesMap,
    collectionsMap,
    fetchCategories,
    fetchSubCategories,
    fetchCollections,
  };
}
